import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import InputField from "components/form/InputField";
import SelectField from "components/form/SelectField";
import TextAreaField from "components/form/TextAreaField";
import DateField from "components/form/DateField";
import FormActionButtons from "components/Button/FormActionButtons";
import AlertDialog from "../../../../components/Feedback/AlertDialog";

import { getAllPositions } from "services/positionService";
import { getAllMemberStatus } from "services/memberStatusService";
import { getAllRoles } from "services/roleService";
import { updateAdmin } from "../../../../services/userServices/adminService";
import { getUserById } from "../../../../services/userServices/userService";

const EditAdmin = () => {
  const navigate = useNavigate();
  const [kodeKaryawan, setKodeKaryawan] = useState("");
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const {
    register,
    handleSubmit,
    //setValue,
    reset,
    //watch,
    control,
    formState: { errors },
  } = useForm();

  const [jabatanOptions, setJabatanOptions] = useState([]);
  const [jenisPenggunaOptions, setJenisPenggunaOptions] = useState([]);
  const [statusKeanggotaanOptions, setStatusKeanggotaanOptions] = useState([]);

  const storedUser = JSON.parse(localStorage.getItem("editAdmin") || "{}");
  const userId = storedUser?.id;

  useEffect(() => {
    if (!userId) {
      console.warn(
        "EditAdmin: userId tidak ditemukan, redirect ke /data-admin"
      );
      navigate("/data-admin");
      return;
    }

    const fetchData = async () => {
      try {
        const [positions, statuses, roles, userResponse] = await Promise.all([
          getAllPositions(),
          getAllMemberStatus(),
          getAllRoles(),
          getUserById(userId),
        ]);

        // debug: lihat bentuk response userResponse
        console.log("EditAdmin - userResponse:", userResponse);

        // jika getAdminById sudah mengembalikan user object langsung
        const user = userResponse?.data ?? userResponse;

        if (!user || Object.keys(user).length === 0)
          throw new Error("Data user tidak ditemukan.");

        // Filter dropdown hanya untuk ADMIN
        const filterAdmin = (arr) =>
          arr
            .filter((item) => item.status?.toUpperCase() === "ADMIN")
            .map((item) => ({ value: item.id, label: item.name }));

        setJabatanOptions(filterAdmin(positions));
        setStatusKeanggotaanOptions(filterAdmin(statuses));
        setJenisPenggunaOptions(filterAdmin(roles));

        reset({
          nama: user.profiles?.full_name ?? user.profiles?.fullName ?? "",
          email: user.email ?? "",
          jabatan:
            user.employees?.position_id ?? user.employees?.positionId ?? "",
          jenis: user.profiles?.roles?.[0]?.id ?? "",
          status:
            user.employees?.member_status_id ??
            user.employees?.memberStatusId ??
            "",
          telp: user.profiles?.phone ?? user.profiles?.phone_number ?? "",
          alamat: user.profiles?.address ?? "",
          tanggalMasuk:
            (
              user.employees?.join_date ??
              user.employees?.joinDate ??
              ""
            )?.substring(0, 10) ?? "",
        });

        setKodeKaryawan(
          user.employees?.employee_id ?? user.employees?.employeeId ?? ""
        );
      } catch (err) {
        console.error("Gagal memuat data admin:", err);
        alert("Terjadi kesalahan saat memuat data.");
        navigate("/data-admin");
      }
    };

    fetchData();
  }, [navigate, reset, userId]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        fullName: data.nama,
        email: data.email,
        phone: data.telp,
        positionId: data.jabatan,
        roleId: data.jenis,
        memberStatusId: data.status,
        employeeId: kodeKaryawan,
        joinDate: data.tanggalMasuk,
        address: data.alamat,
        userType: "ADMIN",
      };
      //console.log("Payload Edit:", payload);
      await updateAdmin(userId, payload);
      localStorage.removeItem("editAdmin");
      setIsAlertOpen(true);
      //console.log("Payload Edit:", payload);
    } catch (err) {
      console.error("Gagal memperbarui admin:", err);
      alert("Terjadi kesalahan saat menyimpan perubahan.");
    }
  };

  return (
    <div className="pl-64 mt-8">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-4xl p-6 mx-auto bg-white border border-gray-200 shadow-lg rounded-2xl"
      >
        <h2 className="mb-6 text-xl font-semibold text-center text-green-700 sm:text-xl">
          Edit Data Admin
        </h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-4">
            <InputField
              label="Nama Lengkap"
              {...register("nama", { required: "Nama wajib diisi" })}
              error={errors.nama}
            />

            <InputField
              label="Email"
              type="email"
              {...register("email", {
                required: "Email wajib diisi",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Format email tidak valid",
                },
              })}
              error={errors.email}
            />

            <SelectField
              label="Jabatan"
              name="jabatan"
              control={control}
              options={jabatanOptions}
              rules={{ required: "Jabatan wajib dipilih" }}
              error={errors.jabatan}
            />

            <SelectField
              label="Jenis Pengguna"
              name="jenis"
              control={control}
              options={jenisPenggunaOptions}
              rules={{ required: "Jenis Pengguna wajib dipilih" }}
              error={errors.jenis}
            />

            <TextAreaField
              label="Alamat"
              {...register("alamat", { required: "Alamat wajib diisi" })}
              error={errors.alamat}
            />
          </div>

          <div className="space-y-4">
            <InputField
              label="No. Telp"
              {...register("telp", {
                required: "No. Telp wajib diisi",
                pattern: {
                  value: /^[0-9]{10,15}$/,
                  message: "Nomor harus 10–15 digit angka",
                },
              })}
              error={errors.telp}
            />

            <InputField
              label="Password"
              type="password"
              readOnly
              value="********"
            />

            <SelectField
              label="Status Keanggotaan"
              name="status"
              control={control}
              options={statusKeanggotaanOptions}
              rules={{ required: "Status wajib dipilih" }}
              error={errors.status}
            />

            <InputField label="Kode Karyawan" value={kodeKaryawan} readOnly />

            <DateField
              label="Tanggal Masuk"
              type="date"
              {...register("tanggalMasuk", {
                required: "Tanggal Masuk wajib diisi",
              })}
              error={errors.tanggalMasuk}
            />
          </div>
        </div>

        <FormActionButtons
          submitLabel="Simpan"
          cancelLabel="Batal"
          onCancel={() => navigate("/data-admin")}
          className="mt-8"
        />
      </form>
      {isAlertOpen && (
        <AlertDialog
          isOpen={isAlertOpen}
          onClose={() => {
            setIsAlertOpen(false);
            navigate("/data-admin"); // pindah ke halaman DataAdmin setelah alert
          }}
          title="Berhasil"
          message="Data admin berhasil diperbarui!"
        />
      )}
    </div>
  );
};

export default EditAdmin;
